import React, {useState} from 'react'
import tw from 'tailwind-styled-components'
import * as types from 'yougile-model'
import {Carousel} from '@components/common/base/carousel/carousel'
import {Padding} from '@components/common/base/design-system/types'
import {Icon} from '@components/common/base/icon'
import {FileViewer} from '@components/file-viewer/file-viewer'
import {FileViewData} from '@extensions/message-utils'
import {getExtensionMediaUrl} from '@extensions/web-platform/get-right-url'

type ExtensionMediaProps = {
  onClickMediaItem?: (item: MediaItem, index: number) => void
  direction?: 'horizontal' | 'vertical'
  extensionId: string
  media: types.ExtensionMedia
  px?: Padding
}

const TwPlayButton = tw.div`
  flex
  justify-center
  items-center
  shrink-0

  w-[31px]
  h-[31px]
  border-solid
  border-1
  border-grey-l60
  rounded-full

  shadow-md
  shadow-black

  group-hover:scale-[1.35]
 
  transition-transform
  duration-200
  ease-out

  bg-primary
`

const TwMediaItem = tw.div`
  group
  flex
  justify-center
  items-center
  w-[224px]
  aspect-[224/126]
  cursor-pointer

  bg-background-disabled
  bg-cover
  bg-center
  bg-no-repeat

  transition-all
  duration-200
  ease-out

  rounded-8
  border-border-base
  border-solid
  border-1
  hover:border-[var(--color-primary-action-default)]
`

const TwVerticalView = tw.div`
  flex
  flex-wrap
  flex-row
  gap-8
`

type MediaItem = {
  id: string
  type: 'video' | 'image'
  imageUrl: string
  videoUrl?: string
}

type CarouselMediaItemProps = {
  media: MediaItem
  onClick?: () => void
}

const CarouselMediaItem = ({media, onClick}: CarouselMediaItemProps) => {
  return (
    <TwMediaItem
      onClick={onClick}
      style={{backgroundImage: `url(${media.imageUrl})`}}
    >
      {Boolean(media.videoUrl) && (
        <TwPlayButton>
          <Icon icon="IconPlayerPlayFilled" color="action-default" size={16} />
        </TwPlayButton>
      )}
    </TwMediaItem>
  )
}

export const ExtensionMedia = ({
  direction = 'horizontal',
  extensionId,
  media,
  px = 0,
  onClickMediaItem
}: ExtensionMediaProps) => {
  const [fileViewerOpenedIndex, setFileViewerOpenedIndex] = useState<
    number | undefined
  >()

  const mediaX: MediaItem[] = media.map((m, index) => {
    if (typeof m === 'string') {
      return {
        id: `${extensionId}_media_${index}`,
        imageUrl: getExtensionMediaUrl(extensionId, m),
        type: 'image'
      }
    }

    if (m.videoUrl) {
      return {
        id: `${extensionId}_media_${index}`,
        imageUrl: getExtensionMediaUrl(extensionId, m.imageUrl),
        videoUrl: getExtensionMediaUrl(extensionId, m.videoUrl),
        type: 'video'
      }
    }

    return {
      id: `${extensionId}_media_${index}`,
      imageUrl: getExtensionMediaUrl(extensionId, m.imageUrl),
      type: 'image'
    }
  })

  const medias = mediaX.map((item, index) => (
    <CarouselMediaItem
      media={item}
      key={item.id + index}
      onClick={() => {
        onClickMediaItem?.(item, index)
        setFileViewerOpenedIndex(index)
      }}
    />
  ))

  return (
    <>
      {direction === 'horizontal' ? (
        <Carousel px={px} scrollMultipler={3.4}>
          {medias}
        </Carousel>
      ) : (
        <TwVerticalView>{medias}</TwVerticalView>
      )}

      {fileViewerOpenedIndex !== undefined && (
        <FileViewer
          autoplay
          files={
            mediaX.map(item => {
              const isVideo = item.type === 'video'

              return {
                url: isVideo ? item.videoUrl : item.imageUrl,
                contentType: item.type,
                timestamp: Date.now()
              }
            }) as FileViewData[]
          }
          selectedIndex={fileViewerOpenedIndex}
          onClose={() => setFileViewerOpenedIndex(undefined)}
        />
      )}
    </>
  )
}
